import { Appearance, PaymentRequest, Stripe, StripeElementsOptions } from '@stripe/stripe-js'

import { TContent, TPlan } from '@/config/types'
import { PaymentMethod } from '@/enums/payments'

import {
  getCommonItemWithExpiration,
  removeCommonItemWithExpiration,
  setCommonItemWithExpiration,
} from './localStorage/helpers'

export function getDefaultStripeOptions(
  planDetails: TPlan | undefined,
  appearance: Appearance
): StripeElementsOptions | undefined {
  if (!planDetails) {
    return
  }

  const amount = planDetails.trialPrice || planDetails.price

  const options: StripeElementsOptions = {
    mode: 'subscription',

    amount: parseFloat((amount * 100).toFixed(2)),
    currency: planDetails.currency.toLowerCase(),
    appearance,
  }

  return options
}

export function getContentDefaultStripeOptions(
  planDetails: TContent | undefined,
  appearance?: Appearance
): StripeElementsOptions | undefined {
  if (!planDetails) {
    return
  }

  const amount = planDetails.price

  const options: StripeElementsOptions = {
    mode: 'payment',

    amount: parseFloat((amount * 100).toFixed(2)),
    currency: planDetails.currency.toLowerCase(),
    appearance,
  }

  return options
}

export type TPaymentMethodsAvailable = Partial<Record<PaymentMethod, boolean>>

const stripeWalletAvailabilityKey = 'sWA'

export function canMakePayment(
  stripe: Stripe,
  paymentRequest?: PaymentRequest,
  force: boolean = false
): Promise<TPaymentMethodsAvailable | null> {
  if (force) {
    removeCommonItemWithExpiration(stripeWalletAvailabilityKey)
  } else {
    const existedResult = getCommonItemWithExpiration(stripeWalletAvailabilityKey, 86400000)
    if (existedResult) {
      return Promise.resolve(JSON.parse(existedResult) as TPaymentMethodsAvailable)
    }
  }

  let pr: PaymentRequest
  if (!paymentRequest) {
    pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Demo total',
        amount: 1,
      },
    })
  } else {
    pr = paymentRequest
  }

  return pr.canMakePayment().then(
    (result) => {
      const data: TPaymentMethodsAvailable = {}
      if (result?.googlePay) {
        data[PaymentMethod.GOOGLE_PAY] = true
      }
      if (result?.applePay) {
        data[PaymentMethod.APPLE_PAY] = true
      }
      setCommonItemWithExpiration(stripeWalletAvailabilityKey, JSON.stringify(data))
      return Promise.resolve(data)
    },
    (error) => Promise.reject(error)
  )
}
